/*
 * multiSelectField — Alpine component for .mh-form-dropdown (multi-select).
 *
 * Popover-style multi-select. Selection state lives on the native
 * <input type="checkbox"> elements inside the panel — they post with
 * the surrounding form natively. We mirror the *summary* (which labels
 * are checked) into Alpine state so the trigger button can display the
 * selected values inline, e.g. "Single Storey, Double Storey".
 *
 * Floating-label support: the trigger carries `data-empty="true"` when
 * no items are selected, which the CSS uses to drop the field label to
 * the placeholder position.
 *
 * Keyboard model mirrors filterSelect:
 *   - Trigger: Space/Enter toggles. ArrowDown opens + focuses first
 *     checkbox; ArrowUp opens + focuses last.
 *   - Panel: ArrowDown/Up roving focus. Home/End jump to first/last.
 *            Space toggles the focused checkbox (native behaviour).
 *            Escape closes + returns focus to the trigger.
 *   - Outside click closes.
 */
export default function multiSelectField() {
    return {
        open: false,
        summary: '',
        empty: true,

        get expandedAttr() {
            return String(this.open);
        },

        get emptyAttr() {
            return String(this.empty);
        },

        init() {
            this.refreshSummary();
            // Recompute summary on every checkbox toggle inside the panel.
            this.$root.addEventListener('change', (event) => {
                if (event.target && event.target.matches('input[type="checkbox"]')) {
                    this.refreshSummary();
                }
            });
        },

        destroy() {},

        refreshSummary() {
            const checked = this.getCheckedInputs();
            this.empty = checked.length === 0;
            this.summary = checked
                .map((input) => this.labelFor(input))
                .filter(Boolean)
                .join(', ');
        },

        getCheckedInputs() {
            if (!this.$refs.panel) return [];
            return Array.from(
                this.$refs.panel.querySelectorAll('input[type="checkbox"]:checked'),
            );
        },

        labelFor(input) {
            const id = input.getAttribute('id');
            if (!id) return '';
            const labelEl = this.$root.querySelector(
                `label.mh-checkbox[for="${CSS.escape(id)}"] .mh-checkbox__label`,
            );
            return labelEl ? labelEl.textContent.trim() : '';
        },

        getItems() {
            if (!this.$refs.panel) return [];
            return Array.from(
                this.$refs.panel.querySelectorAll(
                    'input[type="checkbox"]:not(:disabled)',
                ),
            );
        },

        focusItem(index) {
            const items = this.getItems();
            if (items.length === 0) return;
            const wrapped = (index + items.length) % items.length;
            items[wrapped].focus();
        },

        focusFirst() {
            this.focusItem(0);
        },

        focusLast() {
            this.focusItem(-1);
        },

        focusNext() {
            const items = this.getItems();
            if (items.length === 0) return;
            const active = items.indexOf(document.activeElement);
            this.focusItem(active + 1);
        },

        focusPrev() {
            const items = this.getItems();
            if (items.length === 0) return;
            const active = items.indexOf(document.activeElement);
            this.focusItem(active === -1 ? items.length - 1 : active - 1);
        },

        toggle() {
            this.open = !this.open;
        },

        close() {
            this.open = false;
        },

        openAndFocusFirst() {
            this.open = true;
            this.$nextTick(() => this.focusFirst());
        },

        openAndFocusLast() {
            this.open = true;
            this.$nextTick(() => this.focusLast());
        },

        handleEscape() {
            if (this.open) {
                this.close();
                this.$refs.trigger.focus();
            }
        },
    };
}
